/**
 * This code is part of the es-toolkit package
 * https://github.com/toss/es-toolkit/blob/main/src/array/uniqBy.ts
 */

/**
 * Returns a new array containing only the unique elements from the original array,
 * based on the values returned by the mapper function.
 *
 * @template T - The type of elements in the array.
 * @template U - The type of mapped elements.
 * @param {T[]} arr - The array to process.
 * @param {(item: T) => U} mapper - The function used to convert the array elements.
 * @returns {T[]} A new array containing only the unique elements from the original array, based on the values returned by the mapper function.
 *
 * @example
 * ```ts
 * uniqBy([1.2, 1.5, 2.1, 3.2, 5.7, 5.3, 7.19], Math.floor);
 * // [1.2, 2.1, 3.2, 5.7, 7.19]
 * ```
 *
 * @example
 * const array = [
 *   { category: 'fruit', name: 'apple' },
 *   { category: 'fruit', name: 'banana' },
 *   { category: 'vegetable', name: 'carrot' },
 * ];
 * uniqBy(array, item => item.category).length
 * // 2
 * ```
 */

export const uniqBy = <T, U>(arr: readonly T[], mapper: (item: T) => U): T[] => {
  const map = new Map<U, T>()

  for (let index = 0; index < arr.length; index++) {
    const item = arr[index]
    const key = mapper(item)

    if (!map.has(key)) {
      map.set(key, item)
    }
  }

  return Array.from(map.values())
}
